import * as React from "react";
import classnames from "classnames";

import {
    ButtonGroup as MuiButtonGroup,
    ButtonGroupProps as MuiButtonGroupProps,
    createStyles,
    makeStyles,
    Popper,
    Grow,
    Paper,
    ClickAwayListener,
    MenuList,
} from "@material-ui/core";
import { Button, ButtonProps } from "..";
import {
    UnusedAriaProps,
    UnusedMuiEvents,
    UnusedMuiProps,
    UnusedMuiStandardProps,
} from "../types";
import { RelodeTheme } from "../styles";
import { ArrowDropDownIcon, MenuItem } from "../..";

const useStyles = makeStyles<
    RelodeTheme,
    ButtonGroupProps,
    ButtonGroupClassMap
>(
    (theme: RelodeTheme) =>
        createStyles({
            reset: {
                padding: "8px 22px",
                "&:hover": {
                    transform: "unset",
                    boxShadow: "unset",
                },
            },
            fullWidthButton: {
                flexGrow: 1,
            },
            fullWidth: {
                width: props => (props.fullWidth ? "100%" : "unset"),
            },
        }),
    {
        name: "ButtonGroup",
    }
);

type ButtonGroupClassMap = "reset" | "fullWidth" | "fullWidthButton";

interface ButtonGroupProps
    extends Omit<
        MuiButtonGroupProps,
        | UnusedAriaProps
        | UnusedMuiEvents
        | UnusedMuiProps
        | UnusedMuiStandardProps
    > {}

export const ButtonGroup = React.forwardRef(
    (props: ButtonGroupProps, ref: any) => {
        const { children, fullWidth, ...passthrough } = props;
        const classes = useStyles(props);

        const renderChildren = () =>
            React.Children.map(
                children,
                (child: React.ReactElement<ButtonProps>, index: number) => {
                    if (!child) return null;
                    return React.cloneElement(child, {
                        className: classnames(
                            classes.reset,
                            classes.fullWidthButton,
                            child.props.className
                        ),
                    });
                }
            );
        return (
            <MuiButtonGroup
                {...passthrough}
                ref={ref}
                className={classes.fullWidth}
            >
                {renderChildren()}
            </MuiButtonGroup>
        );
    }
);

export interface ButtonGroupOption {
    id: string;
    label: string;
}
interface ButtonGroupSplitProps<T> extends Omit<ButtonGroupProps, "children"> {
    onSelect: (option: T) => void;
    options: T[];
    renderOption?: (option: T) => React.ReactNode;
    label: string;
    size?: "small" | "medium" | "large";
}

export const ButtonGroupSplit = <T,>(props: ButtonGroupSplitProps<T>) => {
    const {
        options,
        onSelect,
        renderOption,
        size,
        label,
        ...passthrough
    } = props;
    const [areOptionsOpen, setAreOptionsOpen] = React.useState(false);
    const groupRef = React.useRef<HTMLDivElement>(null);

    const handleOptionClick = (opt: T) => {
        setAreOptionsOpen(false);
        onSelect(opt);
    };

    return (
        <>
            <ButtonGroup {...passthrough} ref={groupRef}>
                <Button
                    onClick={() => setAreOptionsOpen(o => !o)}
                    variant="contained"
                    color="primary"
                    size={size}
                >
                    {label}
                </Button>
                <Button
                    style={{ flexGrow: 0 }}
                    onClick={() => setAreOptionsOpen(o => !o)}
                    variant="contained"
                    color="primary"
                    size={size}
                >
                    <ArrowDropDownIcon />
                </Button>
            </ButtonGroup>
            <Popper
                open={areOptionsOpen}
                transition
                disablePortal
                anchorEl={groupRef.current}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: "left",
                        }}
                    >
                        <Paper>
                            <ClickAwayListener
                                onClickAway={() => setAreOptionsOpen(false)}
                            >
                                <MenuList
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    {options.map((opt, index) => (
                                        <MenuItem
                                            key={index}
                                            onClick={() =>
                                                handleOptionClick(opt)
                                            }
                                        >
                                            {!!renderOption
                                                ? renderOption(opt)
                                                : opt.toString()}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
};
